import '../whyDidYouRender'
import axios from 'axios'
import React, { useEffect } from 'react'
import { withLDProvider } from 'launchdarkly-react-client-sdk'
import PropTypes from 'prop-types'
import { Provider } from 'react-redux'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { datadogRum } from '@datadog/browser-rum'
import AppWithReduxStore from '../lib/AppWithReduxStore'
import * as ga from '../lib/google-analytics'
import Auth from '../lib/Auth'
import { useAuth } from '../lib/auth/hooks'

import '../lib/i18n'

const App = ({ Component, pageProps, reduxStore }) => {
  const router = useRouter()
  const auth = useAuth()

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') {
      datadogRum.init({
        version: process.env.NEXT_PUBLIC_WEB_VERSION,
        applicationId: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID,
        clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
        site: 'datadoghq.com',
        service: 'next-web',
        env: process.env.NEXT_PUBLIC_DATADOG_ENV,
        sampleRate: 100,
        trackInteractions: true,
        defaultPrivacyLevel: 'mask-user-input',
        sessionReplaySampleRate: 0
      })
    }
  }, [])

  useEffect(() => {
    if (auth?.user?._id) {
      datadogRum.setUser({
        id: auth?.user?._id,
        username: auth?.user?.username
      })
    } else {
      datadogRum.clearUser()
    }
  }, [auth])

  // set default authorization token
  const token = Auth.getApiToken()
  axios.defaults.headers.common.Authorization = `bearer ${token}`

  useEffect(() => {
    // We inject the auth from the cookie
    if (auth) {
      Auth.initializeWith(auth)
    } else if (typeof window !== 'undefined' && Auth.isUserApiAuthenticated()) {
      // If user is authenticated on the client but not on the server, log out the client too
      Auth.removeUserID()
      Auth.removeUsername()
      Auth.removeApiToken()
    }

    axios.defaults.headers.common.Authorization = `bearer ${Auth.getApiToken()}`
  }, [auth])

  useEffect(() => {
    const handleRouteChange = (url) => {
      ga.pageview(url)
    }

    // this will check for any redirection urls if found
    router.events.on('routeChangeComplete', handleRouteChange)

    // unsubscribe event whenever the component unmounts
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events])

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, shrink-to-fit=yes"
        />
      </Head>
      <Provider store={reduxStore}>
        <Component {...pageProps} />
      </Provider>
    </>
  )
}

App.propTypes = {
  Component: PropTypes.any,
  pageProps: PropTypes.any,
  reduxStore: PropTypes.any,
  router: PropTypes.objectOf(PropTypes.any).isRequired
}

export default withLDProvider({
  clientSideID: process.env.NEXT_PUBLIC_LAUNCHDARKLY_SDK_CLIENT_SIDE_ID,
  /**
   * Initialize the app with an anonymous user.
   */
  user: {
    key: 'anonymous',
    anonymous: true
  }
})(AppWithReduxStore(App))
